import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { Icon } from "@iconify/react";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { getFormInputType } from "../api/form-input-typeAPI";
import {
  getFormDataByWorkOrderFormId,
  updateFormData,
} from "../api/form-dataAPI";
import {
  updateWorkOrderForm,
  generateWorkOrderFormPdf,
} from "../api/work-order-formsAPI";
import { useUserProfile } from "../masterLayout/MasterLayoutBody";
import { getUsersById } from "../api/usersAPI";
import {
  getPaginatedAssets,
  getPaginatedAssetsAndAreas,
  getAssetById,
} from "../api/assetAPI";
import { getCustomers } from "../api/customersAPI";
import { getAreas, getAreaById } from "../api/areaAPI";
import { getAssetMetersByAssetId } from "../api/asset-meterAPI";
import { getProjects } from "../api/projectsAPI";
import {
  createNotification,
  updateNotification,
  getNotificationById,
} from "../api/notificationsAPI";
import NotificationFormModal from "./notification/NotificationFormModal";
import { buildNotificationFields } from "./notification/notificationFormUtils";
import { useFormBuilder } from "../utils/useFormBuilder";
import { useSetting } from "../utils/AppSettingsContext";
import { validateForm } from "../utils/useValidator";
import {
  toLocalInputFormat,
  toUtcIso,
  toUtcIsoWithTime,
} from "../utils/dateUtils";

const parseMinimumPhotosValue = (value) => {
  if (value === undefined || value === null) return 0;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "" || trimmed.toLowerCase() === "n/a") return 0;
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) return 0;
    return Math.max(0, Math.floor(parsed));
  }
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.floor(parsed));
};

const getPhotoRequirementInfo = (item) => {
  if (!item) {
    return {
      minimumFromData: 0,
      requiredCount: 0,
    };
  }
  const minimumFromData = parseMinimumPhotosValue(item?.data?.minimumPhotos);
  const requiredCount = Math.max(minimumFromData, item?.required === 1 ? 1 : 0);
  return { minimumFromData, requiredCount };
};

const getMinimumPhotoValidationMessage = (count) =>
  `A minimum of '${count}' photos is required`;

const ASSET_PAGE_SIZE = 25;

const MeterReadingField = ({
  item,
  assetId,
  resolveMeterForAsset,
  isReadOnly,
  onLocalChange,
  onSave,
}) => {
  const [meter, setMeter] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!assetId) {
      setMeter(null);
      setLoading(false);
      return () => {
        active = false;
      };
    }
    setLoading(true);
    resolveMeterForAsset(assetId)
      .then((info) => {
        if (!active) return;
        setMeter(info?.meter || null);
      })
      .catch(() => {
        if (!active) return;
        setMeter(null);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [assetId, resolveMeterForAsset]);

  if (loading) {
    return <div>Loading meter information...</div>;
  }

  if (!meter) {
    return <div>Asset does not have a meter.</div>;
  }

  const meterName = meter?.meter?.name || meter?.name || "";
  const unitCode =
    meter?.meter?.measurementUnits?.unitCode ||
    meter?.measurementUnits?.unitCode ||
    "";
  const value = item.result || "";

  const handleChange = (event) => {
    const val = event.target.value;
    if (val === "" || (/^\d+$/.test(val) && parseInt(val, 10) > 0)) {
      onLocalChange(item.id, val);
    }
  };

  const handleBlur = (event) => {
    const val = event.target.value;
    if (/^\d+$/.test(val) && parseInt(val, 10) > 0) {
      onSave(item.id, val);
    }
  };

  return (
    <div>
      <div className="mb-2">
        {meterName}
        {unitCode ? ` (${unitCode})` : ""}
      </div>
      <input
        type="number"
        min="1"
        step="1"
        className="form-control"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={isReadOnly}
      />
    </div>
  );
};

const FormViewModal = ({ workOrderFormId, onClose, readOnly = false }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [inputTypes, setInputTypes] = useState([]);
  const [workOrderForm, setWorkOrderForm] = useState(null);
  const [formCompleted, setFormCompleted] = useState(false);
  const [createdByUser, setCreatedByUser] = useState(null);
  const [updatedByUser, setUpdatedByUser] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [projects, setProjects] = useState([]);
  const [assetOptions, setAssetOptions] = useState([]);
  const [assetLoading, setAssetLoading] = useState(false);
  const [incompleteItemIds, setIncompleteItemIds] = useState([]);
  const [incompleteItemMessages, setIncompleteItemMessages] = useState({});
  const userProfile = useUserProfile();
  const useNotificationsSetting = useSetting("use_notifications", 1);
  const useWorkOrderNumberForDefects = Number(useNotificationsSetting) === 0;
  const isReadOnly = readOnly || formCompleted;
  const meterCacheRef = useRef(new Map());
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationForm, setNotificationForm] = useState({});
  const [notificationErrors, setNotificationErrors] = useState({});
  const [notificationSelectedAssign, setNotificationSelectedAssign] =
    useState(null);
  const [notificationEditingId, setNotificationEditingId] = useState(null);
  const [notificationImageModalSrc, setNotificationImageModalSrc] =
    useState(null);
  const [activeNotificationItemId, setActiveNotificationItemId] =
    useState(null);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationEditingIndex, setNotificationEditingIndex] =
    useState(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [showResultImageModal, setShowResultImageModal] = useState(false);
  const [selectedResultImageIndex, setSelectedResultImageIndex] = useState(0);
  const [resultImageZoom, setResultImageZoom] = useState(1);
  const [resultImagePan, setResultImagePan] = useState({ x: 0, y: 0 });
  const [isDraggingResultImage, setIsDraggingResultImage] = useState(false);
  const [canPanResultImage, setCanPanResultImage] = useState(false);
  const resultImageViewportRef = useRef(null);
  const resultImageRef = useRef(null);
  const resultImageDragOffsetRef = useRef({ x: 0, y: 0 });
  const resultImageListRef = useRef(null);
  const resultImageThumbnailRefs = useRef({});
  const resultImageGroupHeaderRefs = useRef({});
  const assetPaginationRef = useRef({
    term: "",
    page: 0,
    hasMore: true,
    isLoading: false,
  });

  const clampResultImagePan = useCallback((x, y) => {
    const viewport = resultImageViewportRef.current;
    const image = resultImageRef.current;
    if (!viewport || !image) {
      return { x: 0, y: 0, canPan: false };
    }

    const viewportRect = viewport.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();
    const scaledWidth = imageRect.width;
    const scaledHeight = imageRect.height;

    const maxX = Math.max(0, (scaledWidth - viewportRect.width) / 2);
    const maxY = Math.max(0, (scaledHeight - viewportRect.height) / 2);

    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
      canPan: maxX > 0 || maxY > 0,
    };
  }, []);

  const markItemIncomplete = useCallback(
    (id, message) => {
      if (!id) return;
      setIncompleteItemIds((prev) =>
        prev.includes(id) ? prev : [...prev, id],
      );
      setIncompleteItemMessages((prev) => {
        if (message) {
          if (prev[id] === message) {
            return prev;
          }
          return { ...prev, [id]: message };
        }
        if (prev[id] !== undefined) {
          const { [id]: _removed, ...rest } = prev;
          return rest;
        }
        return prev;
      });
    },
    [setIncompleteItemIds, setIncompleteItemMessages],
  );

  const clearItemIncomplete = useCallback(
    (id) => {
      if (!id) return;
      setIncompleteItemIds((prev) => prev.filter((itemId) => itemId !== id));
      setIncompleteItemMessages((prev) => {
        if (prev[id] === undefined) {
          return prev;
        }
        const { [id]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [setIncompleteItemIds, setIncompleteItemMessages],
  );

  const resolveMeterForAsset = useCallback(async (assetId) => {
    if (!assetId) return { meter: null, meters: [] };
    if (meterCacheRef.current.has(assetId)) {
      return meterCacheRef.current.get(assetId);
    }
    try {
      const response = await getAssetMetersByAssetId(assetId);
      const meters = Array.isArray(response) ? response : [];
      const primaryMeter =
        meters.find((m) => Number(m.primaryMeter) === 1) || meters[0] || null;
      const info = { meter: primaryMeter, meters };
      meterCacheRef.current.set(assetId, info);
      return info;
    } catch (err) {
      console.error("Error loading meters for asset", err);
      const info = { meter: null, meters: [] };
      meterCacheRef.current.set(assetId, info);
      return info;
    }
  }, []);

  const toImageArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean);

    if (typeof val === "string") {
      // Try JSON first: '["dev/modelimages/..",".."]'
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) return parsed.filter(Boolean);
      } catch (_) {}
      // Fallback for legacy CSV or single string: "a,b" or "a"
      return val
        .split(",")
        .map((s) => s.replace(/^"|"$/g, "").trim())
        .filter(Boolean);
    }
    return [];
  };

  const normaliseIdList = (value) => {
    if (value === undefined || value === null) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (error) {
        // Ignore JSON parse errors and fall through to comma-separated parsing
      }
      if (value.includes(",")) {
        return value
          .split(",")
          .map((part) => part.trim())
          .filter(Boolean);
      }
      return [value];
    }
    return [value];
  };

  const toStringArray = (value, { splitCsv = true } = {}) => {
    if (value === undefined || value === null) return [];
    if (Array.isArray(value)) {
      return value
        .map((item) =>
          item === undefined || item === null ? "" : String(item).trim(),
        )
        .filter(Boolean);
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) =>
              item === undefined || item === null ? "" : String(item).trim(),
            )
            .filter(Boolean);
        }
      } catch (error) {
        // Ignore JSON parse errors and fall through to comma-separated parsing
      }
      if (splitCsv && trimmed.includes(",")) {
        return trimmed
          .split(",")
          .map((part) => part.trim())
          .filter(Boolean);
      }
      return [trimmed];
    }
    if (typeof value === "object" && value) {
      if ("label" in value && value.label) {
        return [String(value.label).trim()].filter(Boolean);
      }
    }
    return [String(value).trim()].filter(Boolean);
  };

  const toNumericIdArray = (value) => {
    const rawList = normaliseIdList(value);
    const numericIds = rawList
      .map((item) => {
        if (item === undefined || item === null) return null;
        if (typeof item === "object") {
          if ("id" in item) {
            const num = Number(item.id);
            return Number.isNaN(num) ? null : num;
          }
          return null;
        }
        const num = Number(item);
        return Number.isNaN(num) ? null : num;
      })
      .filter((num) => typeof num === "number" && !Number.isNaN(num));
    return Array.from(new Set(numericIds));
  };

  const parseDataField = (raw) => {
    const normaliseObject = (obj) => {
      if (!obj || typeof obj !== "object" || Array.isArray(obj)) return {};
      const copy = { ...obj };
      if (copy.defectId !== undefined && copy.defectId !== null) {
        copy.defectId = String(copy.defectId);
      }
      if (copy.workRequestIds !== undefined) {
        copy.workRequestIds = toNumericIdArray(copy.workRequestIds);
      } else if (
        copy.defectId !== undefined &&
        copy.defectId !== null &&
        copy.defectId !== ""
      ) {
        const fallbackIds = toNumericIdArray([copy.defectId]);
        if (fallbackIds.length > 0) {
          copy.workRequestIds = fallbackIds;
        }
      }
      if (copy.minimumPhotos !== undefined && copy.minimumPhotos !== null) {
        const normalisedMinimum = parseMinimumPhotosValue(copy.minimumPhotos);
        copy.minimumPhotos = String(normalisedMinimum);
      }
      return copy;
    };

    if (!raw) {
      return {};
    }

    if (Array.isArray(raw)) {
      return raw.slice();
    }

    if (typeof raw === "string") {
      const trimmed = raw.trim();
      if (trimmed === "") {
        return {};
      }
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.slice();
        }
        if (parsed && typeof parsed === "object") {
          return normaliseObject(parsed);
        }
      } catch (error) {
        return {};
      }
      return {};
    }

    if (typeof raw === "object") {
      return normaliseObject(raw);
    }

    return {};
  };

  const getPrimaryWorkRequestId = (data) => {
    if (!data || typeof data !== "object") return null;
    const ids = toNumericIdArray(data.workRequestIds);
    if (ids.length > 0) return ids[0];
    if (
      data.defectId !== undefined &&
      data.defectId !== null &&
      data.defectId !== ""
    ) {
      const fallback = toNumericIdArray([data.defectId]);
      if (fallback.length > 0) {
        return fallback[0];
      }
    }
    return null;
  };

  const assetAssignmentFilters = useMemo(() => {
    const assignment =
      (Array.isArray(form?.tblFormAssignments)
        ? form.tblFormAssignments[0]
        : null) ||
      form?.formAssignment ||
      null;

    const rawGroupIds = toNumericIdArray(assignment?.assetGroupIds);
    const rawClassIds = toNumericIdArray(assignment?.assetClassIds);
    const allowAllGroups = rawGroupIds.includes(-1);
    const allowAllClasses = rawClassIds.includes(-1);

    return {
      groupIds: allowAllGroups ? [] : rawGroupIds,
      classIds: allowAllClasses ? [] : rawClassIds,
      allowAllGroups,
      allowAllClasses,
    };
  }, [form]);

  const isAssetAllowedByAssignment = useCallback(
    (asset) => {
      const groupId = Number(asset?.groupId);
      const classId = Number(asset?.group?.classId);
      const { allowAllGroups, allowAllClasses, groupIds, classIds } =
        assetAssignmentFilters;

      const groupMatches =
        allowAllGroups ||
        groupIds.length === 0 ||
        (Number.isFinite(groupId) && groupIds.includes(groupId));

      const classMatches =
        allowAllClasses ||
        classIds.length === 0 ||
        (Number.isFinite(classId) && classIds.includes(classId));

      return groupMatches && classMatches;
    },
    [assetAssignmentFilters],
  );

  useEffect(() => {
    if (!workOrderFormId) return;
    (async () => {
      try {
        const [data, types] = await Promise.all([
          getFormDataByWorkOrderFormId(workOrderFormId),
          getFormInputType(),
        ]);

        const getType = (id) =>
          (
            types.find((t) => t.id === id)?.inputType ||
            types.find((t) => t.id === id)?.name ||
            ""
          ).toLowerCase();

        const processed = await Promise.all(
          (data || [])
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(async (it) => {
              let selectedAsset = null;
              const inputType = getType(it.formInputTypeId);
              if (inputType === "assetdetails" && it.assetId) {
                let asset = it.asset;
                if (!asset) {
                  try {
                    asset = await getAssetById(it.assetId);
                  } catch {}
                }
                if (asset) {
                  selectedAsset = {
                    value: asset.id,
                    label: `${asset.assetNumber} - ${asset.assetDescription}`,
                  };
                  if (!it.result) {
                    try {
                      await updateFormData(it.id, {
                        result: `${asset.assetNumber} - ${asset.assetDescription}`,
                        updatedAt: new Date().toISOString(),
                        updatedBy: userProfile?.id,
                      });
                    } catch (err) {
                      console.error("Error saving asset selection", err);
                    }
                    it.result = `${asset.assetNumber} - ${asset.assetDescription}`;
                  }
                }
              }

              let images = toImageArray(it.resultImages);
              if (images.length === 0 && inputType === "photo") {
                images = toImageArray(it.result);
              }
              const looksLikeImageKey = (s) =>
                typeof s === "string" &&
                (/\.(png|jpe?g|webp|heic)$/i.test(s) || s.includes("/"));
              images = (images || []).filter(looksLikeImageKey);

              const parsedData = parseDataField(it.data);
              let defectNotification = null;
              let workRequestNotifications = [];
              const workRequestIds = toNumericIdArray(
                parsedData?.workRequestIds,
              );

              if (inputType === "workrequest") {
                if (workRequestIds.length > 0) {
                  const loaded = await Promise.all(
                    workRequestIds.map(async (requestId) => {
                      try {
                        return await getNotificationById(requestId);
                      } catch (error) {
                        console.error(
                          "Error loading linked work request",
                          error,
                        );
                        return null;
                      }
                    }),
                  );
                  workRequestNotifications = loaded.filter(Boolean);
                }
              } else if (
                ["yesno", "okdefect", "passfail"].includes(inputType)
              ) {
                const primaryId = workRequestIds[0];
                if (primaryId) {
                  try {
                    defectNotification = await getNotificationById(primaryId);
                  } catch (error) {
                    console.error("Error loading linked notification", error);
                  }
                }
              }

              const hasResult =
                it.result !== null &&
                it.result !== undefined &&
                !(typeof it.result === "string" && it.result.trim() === "");
              const baseResult = hasResult
                ? it.result
                : selectedAsset
                  ? selectedAsset.label
                  : inputType === "workrequest" &&
                      workRequestNotifications.length > 0
                    ? String(workRequestNotifications.length)
                    : "";
              const normalizedResult =
                inputType === "date"
                  ? toLocalInputFormat(baseResult, false) || baseResult
                  : inputType === "datetime" || inputType === "datetime-local"
                    ? toLocalInputFormat(baseResult, true) || baseResult
                    : baseResult;

              return {
                ...it,
                result: normalizedResult,
                resultImages: images,
                comments: it.comments || "",
                showComment: Boolean(it.comments),
                selectedAsset,
                data: parsedData,
                defectNotification,
                workRequestNotifications,
              };
            }),
        );

        setItems(processed);
        setForm(data[0]?.form || null);
        setInputTypes(types);
        const woForm = data[0]?.workOrderForm || null;
        setWorkOrderForm(woForm);
        setFormCompleted(Boolean(woForm?.completed));
        if (woForm?.createdBy) {
          try {
            const user = await getUsersById(woForm.createdBy);
            setCreatedByUser(user);
          } catch {
            setCreatedByUser(null);
          }
        }
        if (woForm?.updatedBy) {
          try {
            const user = await getUsersById(woForm.updatedBy);
            setUpdatedByUser(user);
          } catch {
            setUpdatedByUser(null);
          }
        }
      } catch (err) {
        console.error("Error loading form data", err);
      }
    })();
  }, [workOrderFormId]);

  useEffect(() => {
    const selectedAssets = items
      .map((it) => it?.selectedAsset)
      .filter(
        (asset) => asset && asset.value !== undefined && asset.value !== null,
      );
    if (!selectedAssets.length) return;

    setAssetOptions((prev) => {
      const map = new Map(prev.map((opt) => [opt.value, opt]));
      let changed = false;
      selectedAssets.forEach((asset) => {
        if (!map.has(asset.value)) {
          map.set(asset.value, asset);
          changed = true;
        }
      });
      if (!changed) {
        return prev;
      }
      return Array.from(map.values());
    });
  }, [items]);

  useEffect(() => {
    (async () => {
      try {
        const [custs, areaData, projResp] = await Promise.all([
          getCustomers().catch(() => []),
          getAreas().catch(() => []),
          getProjects().catch(() => []),
        ]);
        setCustomers(Array.isArray(custs) ? custs : []);
        setAreas(Array.isArray(areaData) ? areaData : []);
        const projectItems = Array.isArray(projResp?.projects)
          ? projResp.projects
          : Array.isArray(projResp)
            ? projResp
            : [];
        setProjects(projectItems);
      } catch (err) {
        console.error("Error loading customers, areas, or projects", err);
      }
    })();
  }, []);

  const handleNotificationAssignSearch = useCallback(async (inputValue) => {
    try {
      const shouldLoadDefault = !inputValue;
      if (!shouldLoadDefault && inputValue.length < 2) return [];
      const resp = await getPaginatedAssetsAndAreas({
        page: 1,
        pageSize: 10,
        search: shouldLoadDefault ? "" : inputValue,
        type: "all",
      });
      const items = resp?.data || resp || [];
      return items.map((entry) => ({
        value: entry.id,
        label: entry.label,
        type: entry.type,
      }));
    } catch (error) {
      console.error("Error fetching assets/areas:", error);
      return [];
    }
  }, []);

  const formatNotificationAssignOption = useCallback(
    (option) => (
      <div className="d-flex justify-content-start align-items-center gap-2">
        <span className="badge bg-secondary ms-2">
          <Icon
            icon={
              option.type === "Asset"
                ? "mage:delivery-truck"
                : "ic:baseline-location-on"
            }
            className="icon text-xl line-height-1"
          />
        </span>
        <span>{option.label}</span>
      </div>
    ),
    [],
  );

  const formatNotificationPriorityOption = useCallback(
    (option) => (
      <div className="d-flex align-items-center gap-2">
        <span
          className={`d-inline-block rounded-circle bg-${option.colour || "secondary"}`}
          style={{ width: "10px", height: "10px" }}
        ></span>
        <span>{option.label}</span>
      </div>
    ),
    [],
  );

  const handleNotificationImageClick = useCallback((src) => {
    setNotificationImageModalSrc(src);
  }, []);

  const notificationFields = useMemo(
    () =>
      buildNotificationFields({
        newNotification: notificationForm,
        selectedAssign: notificationSelectedAssign,
        setSelectedAssign: setNotificationSelectedAssign,
        handleAssignSearch: handleNotificationAssignSearch,
        formatAssignOption: formatNotificationAssignOption,
        formatPriorityOption: formatNotificationPriorityOption,
        formReadOnly: false,
        editingId: notificationEditingId,
        onImageClick: handleNotificationImageClick,
        numberLabel: useWorkOrderNumberForDefects
          ? "Work Order Number"
          : "Notification Number",
      }),
    [
      notificationForm,
      notificationSelectedAssign,
      handleNotificationAssignSearch,
      formatNotificationAssignOption,
      formatNotificationPriorityOption,
      notificationEditingId,
      handleNotificationImageClick,
      useWorkOrderNumberForDefects,
    ],
  );

  const { renderField: renderNotificationField } = useFormBuilder(
    notificationFields,
    notificationForm,
    setNotificationForm,
    notificationErrors,
  );

  const getInputType = useCallback(
    (id) => {
      return (
        inputTypes.find((t) => t.id === id)?.inputType ||
        inputTypes.find((t) => t.id === id)?.name ||
        ""
      ).toLowerCase();
    },
    [inputTypes],
  );

  const selectedAssetId = useMemo(() => {
    const assetItem = items.find(
      (item) =>
        getInputType(item.formInputTypeId) === "assetdetails" && item.assetId,
    );
    return assetItem?.assetId || null;
  }, [items, getInputType]);

  const getInputTypeMeta = useCallback(
    (id) => {
      const type = inputTypes.find((t) => t.id === id);
      let data = type?.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (error) {
          data = {};
        }
      }
      if (!data || typeof data !== "object" || Array.isArray(data)) {
        data = {};
      }
      return {
        key: (type?.inputType || type?.name || "").toLowerCase(),
        data,
      };
    },
    [inputTypes],
  );

  const handleLocalChange = (id, value, extra = {}) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id ? { ...it, result: value, ...extra } : it,
      ),
    );
    if (value && String(value).trim() !== "") {
      clearItemIncomplete(id);
    }
  };

  const saveResult = async (id, value, extra = {}) => {
    try {
      const item = items.find((it) => it.id === id);
      const inputType = item ? getInputType(item.formInputTypeId) : "";
      const normalizedValue = !value
        ? value
        : inputType === "date"
          ? (toUtcIso(value) ?? value)
          : inputType === "datetime" || inputType === "datetime-local"
            ? (toUtcIsoWithTime(value) ?? value)
            : value;
      await updateFormData(id, {
        result: normalizedValue,
        updatedAt: new Date().toISOString(),
        updatedBy: userProfile?.id,
        ...extra,
      });
    } catch (err) {
      console.error("Error saving form data", err);
    }
  };

  const handlePhotoUpload = async (id, files) => {
    const uploaded = [];
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/upload/modelimages`,
          {
            method: "POST",
            body: fd,
            credentials: "include",
          },
        );
        const data = await res.json();
        uploaded.push(data.key);
      } catch (err) {
        console.error("Image upload failed", err);
      }
    }
    if (uploaded.length > 0) {
      const existingItem = items.find((it) => it.id === id) || null;
      const existingImages = existingItem?.resultImages || [];
      const newImages = [...existingImages, ...uploaded];
      setItems((prev) =>
        prev.map((it) =>
          it.id === id ? { ...it, resultImages: newImages } : it,
        ),
      );
      const { minimumFromData, requiredCount } =
        getPhotoRequirementInfo(existingItem);
      if (requiredCount > 0 && newImages.length < requiredCount) {
        const message =
          minimumFromData > 0 && newImages.length < minimumFromData
            ? getMinimumPhotoValidationMessage(minimumFromData)
            : undefined;
        markItemIncomplete(id, message);
      } else {
        clearItemIncomplete(id);
      }
      try {
        await updateFormData(id, {
          resultImages: newImages,
          updatedAt: new Date().toISOString(),
          updatedBy: userProfile?.id,
        });
      } catch (err) {
        console.error("Error saving images", err);
      }
    }
  };

  const handleRemovePhoto = async (id, index) => {
    const existingItem = items.find((it) => it.id === id) || null;
    const existingImages = existingItem?.resultImages || [];
    const newImages = existingImages.filter((_, i) => i !== index);
    setItems((prev) =>
      prev.map((it) =>
        it.id === id ? { ...it, resultImages: newImages } : it,
      ),
    );
    const { minimumFromData, requiredCount } =
      getPhotoRequirementInfo(existingItem);
    if (requiredCount > 0 && newImages.length < requiredCount) {
      const message =
        minimumFromData > 0 && newImages.length < minimumFromData
          ? getMinimumPhotoValidationMessage(minimumFromData)
          : undefined;
      markItemIncomplete(id, message);
    } else {
      clearItemIncomplete(id);
    }
    try {
      await updateFormData(id, {
        resultImages: newImages,
        updatedAt: new Date().toISOString(),
        updatedBy: userProfile?.id,
      });
    } catch (err) {
      console.error("Error removing image", err);
    }
  };

  const handleCommentToggle = (id) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, showComment: true } : it)),
    );
  };

  const handleCommentChange = (id, value) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, comments: value } : it)),
    );
  };

  const saveComment = async (id, value) => {
    try {
      await updateFormData(id, {
        comments: value,
        updatedAt: new Date().toISOString(),
        updatedBy: userProfile?.id,
      });
    } catch (err) {
      console.error("Error saving comment", err);
    }
  };

  useEffect(() => {
    if (!Array.isArray(items) || items.length === 0) return;
    items.forEach((item) => {
      const typeMeta = inputTypes.find((t) => t.id === item.formInputTypeId);
      const type = (typeMeta?.inputType || typeMeta?.name || "").toLowerCase();
      if (type !== "photo") return;
      const { minimumFromData, requiredCount } = getPhotoRequirementInfo(item);
      if (minimumFromData <= 0) return;
      const imageCount = Array.isArray(item.resultImages)
        ? item.resultImages.length
        : 0;
      if (imageCount < requiredCount) {
        const message = getMinimumPhotoValidationMessage(minimumFromData);
        markItemIncomplete(item.id, message);
      } else {
        clearItemIncomplete(item.id);
      }
    });
  }, [items, inputTypes, markItemIncomplete, clearItemIncomplete]);

  useEffect(() => {
    setNotificationForm((prev) => ({
      ...prev,
      assignable: notificationSelectedAssign?.value ?? null,
    }));
    if (!notificationSelectedAssign) return;
    const type = notificationSelectedAssign.type?.toLowerCase();
    if (type === "asset") {
      setNotificationForm((prev) => ({
        ...prev,
        assetId: notificationSelectedAssign.value,
        areaId: null,
      }));
      getAssetById(notificationSelectedAssign.value)
        .then((asset) => {
          if (asset?.assetDepartmentId) {
            setNotificationForm((prev) => ({
              ...prev,
              departmentId: asset.assetDepartmentId,
            }));
          }
        })
        .catch((error) =>
          console.error("Error loading asset department", error),
        );
    } else if (type === "location" || type === "area") {
      setNotificationForm((prev) => ({
        ...prev,
        areaId: notificationSelectedAssign.value,
        assetId: null,
      }));
      getAreaById(notificationSelectedAssign.value)
        .then((area) => {
          if (area?.departmentId) {
            setNotificationForm((prev) => ({
              ...prev,
              departmentId: area.departmentId,
            }));
          }
        })
        .catch((error) =>
          console.error("Error loading area department", error),
        );
    }
  }, [notificationSelectedAssign]);

  const openNotificationModalForItem = useCallback(
    async (item, notificationId, notificationIndex = null) => {
      if (!item) return;
      const idToLoad = notificationId ? Number(notificationId) : null;
      setNotificationLoading(true);
      setNotificationErrors({});
      setNotificationEditingId(idToLoad);
      setActiveNotificationItemId(item.id);
      setNotificationEditingIndex(notificationIndex ?? null);

      let baseNotification = {};
      let assignOption = null;

      try {
        if (idToLoad) {
          let existing = null;
          if (
            item.defectNotification &&
            Number(item.defectNotification.id) === idToLoad
          ) {
            existing = item.defectNotification;
          } else if (Array.isArray(item.workRequestNotifications)) {
            existing =
              item.workRequestNotifications.find(
                (notification) => Number(notification.id) === idToLoad,
              ) || null;
          } else {
            existing = await getNotificationById(idToLoad);
          }
          if (existing) {
            baseNotification = { ...existing };
            if (useWorkOrderNumberForDefects) {
              const linkedWorkOrderNumber =
                existing?.tblWorkOrders?.workOrderNumber ||
                existing?.workOrder?.workOrderNumber ||
                "";
              if (linkedWorkOrderNumber) {
                baseNotification.workOrderNumber = linkedWorkOrderNumber;
                baseNotification.notificationNumber = linkedWorkOrderNumber;
              }
            }
            if (existing.assetId) {
              try {
                const asset =
                  existing.asset || (await getAssetById(existing.assetId));
                if (asset) {
                  assignOption = {
                    value: asset.id,
                    label: `${asset.assetNumber} – ${asset.assetDescription}`,
                    type: "Asset",
                  };
                }
              } catch (error) {
                console.error("Error loading notification asset", error);
              }
            } else if (existing.areaId) {
              try {
                const area =
                  existing.area || (await getAreaById(existing.areaId));
                if (area) {
                  const code = area.code || area.areaCode || "";
                  const name = area.name || "";
                  const label = code
                    ? `${code} - ${name}`
                    : name || `Area ${area.id}`;
                  assignOption = {
                    value: area.id,
                    label,
                    type: "Location",
                  };
                }
              } catch (error) {
                console.error("Error loading notification area", error);
              }
            }
          }
        } else {
          baseNotification = {
            notificationNumber: "Auto-generated on save",
            workOrderNumber: "Auto-generated on save",
            priorityId: 3,
          };
          if (item?.description) {
            baseNotification.notificationTitle = item.description;
          }
          if (item?.comments) {
            baseNotification.notificationDescription = item.comments;
          }
          if (workOrderForm?.assetId) {
            try {
              const asset =
                workOrderForm.asset ||
                (await getAssetById(workOrderForm.assetId));
              if (asset) {
                assignOption = {
                  value: asset.id,
                  label: `${asset.assetNumber} – ${asset.assetDescription}`,
                  type: "Asset",
                };
                baseNotification.assetId = asset.id;
                baseNotification.areaId = null;
                if (asset.assetDepartmentId) {
                  baseNotification.departmentId = asset.assetDepartmentId;
                }
              }
            } catch (error) {
              console.error("Error preloading asset for notification", error);
            }
          } else if (workOrderForm?.areaId) {
            try {
              const area =
                workOrderForm.area || (await getAreaById(workOrderForm.areaId));
              if (area) {
                const code = area.code || area.areaCode || "";
                const name = area.name || "";
                const label = code
                  ? `${code} - ${name}`
                  : name || `Area ${area.id}`;
                assignOption = {
                  value: area.id,
                  label,
                  type: "Location",
                };
                baseNotification.areaId = area.id;
                baseNotification.assetId = null;
                if (area.departmentId) {
                  baseNotification.departmentId = area.departmentId;
                }
              }
            } catch (error) {
              console.error("Error preloading area for notification", error);
            }
          }
        }
      } catch (error) {
        console.error("Error preparing notification form", error);
      } finally {
        setNotificationForm(baseNotification);
        setNotificationSelectedAssign(assignOption || null);
        setShowNotificationModal(true);
        setNotificationLoading(false);
      }
    },
    [workOrderForm, useWorkOrderNumberForDefects],
  );

  const closeNotificationModal = useCallback(() => {
    setShowNotificationModal(false);
    setNotificationForm({});
    setNotificationErrors({});
    setNotificationSelectedAssign(null);
    setNotificationEditingId(null);
    setNotificationImageModalSrc(null);
    setActiveNotificationItemId(null);
    setNotificationLoading(false);
    setNotificationSaving(false);
    setNotificationEditingIndex(null);
  }, []);

  const isRaiseDefectEnabled = useCallback((item) => {
    if (!item) return false;
    const raw =
      item.raiseDefects ?? item.data?.raiseDefects ?? item.data?.raiseDefect;
    if (raw === 1 || raw === "1") return true;
    if (raw === 0 || raw === "0") return false;
    if (typeof raw === "boolean") return raw;
    if (typeof raw === "string") {
      const normalised = raw.trim().toLowerCase();
      if (["true", "yes", "y", "on"].includes(normalised)) return true;
      if (["false", "no", "n", "off"].includes(normalised)) return false;
    }
    return Boolean(raw);
  }, []);

  const maybeOpenDefectModal = useCallback(
    (item, value, label) => {
      if (isReadOnly) return;
      if (!isRaiseDefectEnabled(item)) return;
      const triggerLabel = getInputTypeMeta(item.formInputTypeId)?.data
        ?.raisedefect;
      if (!triggerLabel) return;
      const normalisedTrigger = String(triggerLabel).trim().toLowerCase();
      const normalisedLabel = label ? String(label).trim().toLowerCase() : "";
      const normalisedValue =
        value === null || value === undefined
          ? ""
          : String(value).trim().toLowerCase();
      if (
        normalisedTrigger &&
        (normalisedTrigger === normalisedLabel ||
          normalisedTrigger === normalisedValue)
      ) {
        const existingId = getPrimaryWorkRequestId(item.data);
        openNotificationModalForItem(item, existingId);
      }
    },
    [
      getInputTypeMeta,
      isReadOnly,
      isRaiseDefectEnabled,
      openNotificationModalForItem,
    ],
  );

  const handleResultOptionSelect = useCallback(
    (item, value, label) => {
      handleLocalChange(item.id, value);
      saveResult(item.id, value);
      maybeOpenDefectModal(item, value, label);
    },
    [handleLocalChange, saveResult, maybeOpenDefectModal],
  );

  const renderNotificationLink = (
    notification,
    onClick,
    {
      icon = "mage:exclamation-triangle-fill",
      label = "View Notification",
      key,
    } = {},
  ) => {
    if (!notification) return null;
    return (
      <div key={key} className="d-flex align-items-center gap-2 mt-3 ">
        <button
          type="button"
          className="btn btn-link p-0 text-secondary d-flex align-items-center gap-2 text-decoration-none"
          onClick={onClick}
        >
          <Icon icon={icon} className="text-warning text-lg" />
          <span className="text-sm fw-semibold text-secondary mt-2">
            {label}
          </span>
        </button>
      </div>
    );
  };

  const renderDefectIndicator = (item) => {
    if (!item?.defectNotification) return null;
    const number = useWorkOrderNumberForDefects
      ? item.defectNotification?.tblWorkOrders?.workOrderNumber ||
        item.defectNotification?.workOrder?.workOrderNumber
      : item.defectNotification.notificationNumber;
    const title = item.defectNotification.notificationTitle;
    const label = [number ? `#${number}` : null, title || null]
      .filter(Boolean)
      .join(" - ");
    const defectId =
      item.defectNotification.id || getPrimaryWorkRequestId(item.data);
    return renderNotificationLink(
      item.defectNotification,
      () => openNotificationModalForItem(item, defectId),
      {
        label: label || "View Notification",
      },
    );
  };

  const renderWorkRequestList = (item) => {
    if (
      !Array.isArray(item?.workRequestNotifications) ||
      item.workRequestNotifications.length === 0
    ) {
      return null;
    }
    return item.workRequestNotifications.map((notification, index) => {
      const number = useWorkOrderNumberForDefects
        ? notification?.tblWorkOrders?.workOrderNumber ||
          notification?.workOrder?.workOrderNumber
        : notification.notificationNumber;
      const title = notification.notificationTitle || "";
      const labelParts = [number ? `#${number}` : null, title || null].filter(
        Boolean,
      );
      const label =
        labelParts.length > 0 ? labelParts.join(" - ") : "View Work Request";
      const key = notification.id || `${item.id}-workrequest-${index}`;
      return renderNotificationLink(
        notification,
        () => openNotificationModalForItem(item, notification.id, index),
        {
          icon: "mage:exclamation-triangle",
          label,
          key,
        },
      );
    });
  };

  const handleNotificationSave = async () => {
    const errs = validateForm(notificationFields, notificationForm);
    setNotificationErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setNotificationSaving(true);
    try {
      const cleanPayload = {};
      for (const key in notificationForm) {
        if (
          [
            "workOrderNumber",
            "notificationStatusId",
            "departmentId",
            "notificationNumber",
          ].includes(key)
        )
          continue;
        if (
          notificationForm[key] !== null &&
          typeof notificationForm[key] !== "object"
        ) {
          cleanPayload[key] = notificationForm[key];
        }
      }

      const type = notificationSelectedAssign?.type?.toLowerCase();
      const payload = {
        ...cleanPayload,
        assetId: type === "asset" ? notificationSelectedAssign?.value : null,
        areaId:
          type === "location" || type === "area"
            ? notificationSelectedAssign?.value
            : null,
        notificationImages: notificationForm.notificationImages,
        notificationStatusId: 4,
        departmentId: notificationForm.departmentId,
      };
      delete payload.assignable;

      let saved;
      if (notificationEditingId) {
        saved = await updateNotification(notificationEditingId, payload);
      } else {
        saved = await createNotification(payload);
      }

      if (activeNotificationItemId) {
        const existingItem = items.find(
          (it) => it.id === activeNotificationItemId,
        );
        const existingData =
          existingItem &&
          existingItem.data &&
          typeof existingItem.data === "object"
            ? { ...existingItem.data }
            : {};
        const itemType = existingItem
          ? getInputType(existingItem.formInputTypeId)
          : "";

        if (itemType === "workrequest") {
          const existingIds = toNumericIdArray(existingData.workRequestIds);
          let nextIds = [...existingIds];
          if (!nextIds.some((id) => Number(id) === Number(saved.id))) {
            nextIds.push(Number(saved.id));
          }
          const currentNotifications = Array.isArray(
            existingItem?.workRequestNotifications,
          )
            ? [...existingItem.workRequestNotifications]
            : [];
          let nextNotifications;
          if (
            notificationEditingId !== null &&
            notificationEditingIndex !== null &&
            notificationEditingIndex >= 0
          ) {
            nextNotifications = [...currentNotifications];
            nextNotifications[notificationEditingIndex] = saved;
          } else if (notificationEditingId !== null) {
            nextNotifications = currentNotifications.map((notification) =>
              Number(notification.id) === Number(saved.id)
                ? saved
                : notification,
            );
          } else {
            nextNotifications = [...currentNotifications, saved];
          }

          const dataPayload = { ...existingData, workRequestIds: nextIds };
          const resultValue =
            nextNotifications.length > 0
              ? String(nextNotifications.length)
              : "";
          try {
            await updateFormData(activeNotificationItemId, {
              data: dataPayload,
              result: resultValue,
              updatedAt: new Date().toISOString(),
              updatedBy: userProfile?.id,
            });
          } catch (error) {
            console.error(
              "Error recording work request ids on form data",
              error,
            );
          }
          setItems((prev) =>
            prev.map((it) =>
              it.id === activeNotificationItemId
                ? {
                    ...it,
                    data: dataPayload,
                    workRequestNotifications: nextNotifications,
                    result: resultValue,
                  }
                : it,
            ),
          );
          if (resultValue && resultValue.trim() !== "") {
            clearItemIncomplete(activeNotificationItemId);
          }
        } else {
          const nextIds = [Number(saved.id)];
          const dataPayload = {
            ...existingData,
            workRequestIds: nextIds,
          };
          delete dataPayload.defectId;
          try {
            await updateFormData(activeNotificationItemId, {
              data: dataPayload,
              updatedAt: new Date().toISOString(),
              updatedBy: userProfile?.id,
            });
          } catch (error) {
            console.error(
              "Error recording notification id on form data",
              error,
            );
          }
          setItems((prev) =>
            prev.map((it) =>
              it.id === activeNotificationItemId
                ? {
                    ...it,
                    data: dataPayload,
                    defectNotification: saved,
                  }
                : it,
            ),
          );
        }
      }

      closeNotificationModal();
    } catch (error) {
      console.error("Error saving notification", error);
    } finally {
      setNotificationSaving(false);
    }
  };

  const handleExportPdf = async () => {
    setIsExportingPdf(true);
    try {
      const resp = await generateWorkOrderFormPdf(workOrderFormId);
      const url = resp?.url || resp?.pdfLink;
      if (url) {
        window.open(url, "_blank", "noopener");
      }
    } catch (err) {
      console.error("Error exporting form PDF", err);
    } finally {
      setIsExportingPdf(false);
    }
  };

  const fetchAssetOptions = useCallback(
    async ({ search, page }) => {
      assetPaginationRef.current = {
        term: search,
        page,
        hasMore: page === 1 ? true : assetPaginationRef.current.hasMore,
        isLoading: true,
      };
      setAssetLoading(true);

      try {
        const resp = await getPaginatedAssets({
          page,
          pageSize: ASSET_PAGE_SIZE,
          search,
          groupId: assetAssignmentFilters.allowAllGroups
            ? undefined
            : assetAssignmentFilters.groupIds,
          classId: assetAssignmentFilters.allowAllClasses
            ? undefined
            : assetAssignmentFilters.classIds,
        });
        const items = Array.isArray(resp?.data)
          ? resp.data
          : Array.isArray(resp)
            ? resp
            : [];
        const filteredItems = items.filter(isAssetAllowedByAssignment);
        const options = filteredItems.map((a) => ({
          value: a.id,
          label: `${a.assetNumber} - ${a.assetDescription}`,
        }));

        const totalCount =
          typeof resp?.total === "number"
            ? resp.total
            : typeof resp?.count === "number"
              ? resp.count
              : undefined;

        let nextOptions = [];
        if (page === 1) {
          nextOptions = options;
        } else {
          const map = new Map(assetOptions.map((opt) => [opt.value, opt]));
          options.forEach((opt) => {
            if (!map.has(opt.value)) {
              map.set(opt.value, opt);
            }
          });
          nextOptions = Array.from(map.values());
        }
        setAssetOptions(nextOptions);

        const hasMore =
          typeof totalCount === "number"
            ? page * ASSET_PAGE_SIZE < totalCount
            : options.length === ASSET_PAGE_SIZE;

        assetPaginationRef.current = {
          term: search,
          page,
          hasMore,
          isLoading: false,
        };
        return nextOptions;
      } catch (err) {
        console.error("Error fetching assets:", err);
        assetPaginationRef.current = {
          term: search,
          page,
          hasMore: false,
          isLoading: false,
        };
        if (page === 1) {
          setAssetOptions([]);
        }
        return [];
      } finally {
        setAssetLoading(false);
      }
    },
    [assetAssignmentFilters, assetOptions, isAssetAllowedByAssignment],
  );

  const handleAssetSearch = useCallback(
    async (inputValue) => {
      const raw = typeof inputValue === "string" ? inputValue : "";
      const trimmed = raw.trim();
      const shouldLoadDefault = trimmed === "";

      if (!shouldLoadDefault && trimmed.length < 2) {
        assetPaginationRef.current = {
          term: trimmed,
          page: 1,
          hasMore: true,
          isLoading: false,
        };
        setAssetOptions([]);
        return [];
      }

      const search = shouldLoadDefault ? "" : trimmed;
      return fetchAssetOptions({ search, page: 1 });
    },
    [fetchAssetOptions],
  );

  const handleAssetMenuScrollToBottom = useCallback(() => {
    const { term, page, hasMore, isLoading } = assetPaginationRef.current;
    if (assetLoading || isLoading || !hasMore) {
      return;
    }

    const search = term || "";
    const shouldLoadDefault = search === "";
    if (!shouldLoadDefault && search.length < 2) {
      return;
    }

    const nextPage = (page || 0) + 1;
    fetchAssetOptions({ search, page: nextPage });
  }, [assetLoading, fetchAssetOptions]);

  const handleAssetSelect = async (id, option) => {
    const nextAssetId = option ? option.value : null;
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? {
              ...it,
              assetId: nextAssetId,
              result: option ? option.label : "",
              selectedAsset: option || null,
            }
          : it,
      ),
    );
    if (option) {
      setAssetOptions((prev) => {
        if (prev.some((opt) => opt.value === option.value)) {
          return prev;
        }
        return [...prev, option];
      });
      clearItemIncomplete(id);
    }
    try {
      await updateFormData(id, {
        assetId: nextAssetId,
        result: option ? option.label : "",
        updatedAt: new Date().toISOString(),
        updatedBy: userProfile?.id,
      });
      await updateWorkOrderForm(workOrderFormId, {
        assetId: nextAssetId,
        updatedAt: new Date().toISOString(),
        updatedBy: userProfile?.id,
      });
      setWorkOrderForm((prev) =>
        prev ? { ...prev, assetId: nextAssetId } : prev,
      );
    } catch (err) {
      console.error("Error saving asset selection", err);
    }
  };

  const SignatureField = ({ item, value }) => {
    const canvasRef = useRef(null);
    const drawing = useRef(false);

    const startDraw = (e) => {
      drawing.current = true;
      const ctx = canvasRef.current.getContext("2d");
      ctx.beginPath();
      ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    };

    const draw = (e) => {
      if (!drawing.current) return;
      const ctx = canvasRef.current.getContext("2d");
      ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.stroke();
    };

    const endDraw = () => {
      drawing.current = false;
    };

    const clearCanvas = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    useEffect(() => {
      clearCanvas();
    }, [value]);

    const saveSignature = async () => {
      try {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dataUrl = canvas.toDataURL("image/jpeg");
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const fd = new FormData();
        fd.append(
          "file",
          new File([blob], `signature-${Date.now()}.jpg`, {
            type: "image/jpeg",
          }),
        );
        const uploadRes = await fetch(
          `${process.env.REACT_APP_API_URL}/upload/workorderfiles`,
          {
            method: "POST",
            body: fd,
            credentials: "include",
          },
        );
        const uploadData = await uploadRes.json();
        handleLocalChange(item.id, uploadData.key);
        await saveResult(item.id, uploadData.key);
      } catch (err) {
        console.error("Signature upload failed", err);
      }
    };

    const handleResign = () => {
      if (canvasRef.current) {
        clearCanvas();
      }
      handleLocalChange(item.id, "");
      saveResult(item.id, "");
    };

    if (isReadOnly && value) {
      return (
        <div
          style={{
            width: "100%",
            height: "150px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src={`${process.env.REACT_APP_SPACES_BASE}/${value}`}
            alt="signature"
            style={{
              maxWidth: "100%",
              height: "100%",
              objectFit: "contain",
              //transform: 'rotate(90deg)',
            }}
          />
        </div>
      );
    }

    if (value) {
      return (
        <div>
          <img
            src={`${process.env.REACT_APP_SPACES_BASE}/${value}`}
            alt="signature"
            style={{
              width: "100%",
              maxHeight: "150px",
              objectFit: "contain",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
          {!isReadOnly && (
            <button
              type="button"
              className="btn btn-link mt-2 p-0"
              onClick={handleResign}
            >
              Clear
            </button>
          )}
        </div>
      );
    }

    return (
      <div>
        <canvas
          ref={canvasRef}
          width={500}
          height={150}
          style={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            width: "100%",
            touchAction: "none",
          }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
        />
        <div className="mt-2 d-flex gap-2">
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={saveSignature}
            disabled={isReadOnly}
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={clearCanvas}
            disabled={isReadOnly}
          >
            Clear
          </button>
        </div>
      </div>
    );
  };

  const renderInput = (item) => {
    const type = getInputType(item.formInputTypeId);
    const value = item.result ?? "";
    switch (type) {
      case "text":
      case "value":
        return (
          <>
            <input
              type="text"
              className="form-control"
              value={value}
              onChange={(e) => handleLocalChange(item.id, e.target.value)}
              onBlur={(e) => saveResult(item.id, e.target.value)}
              disabled={isReadOnly}
            />
          </>
        );
      case "textbox":
        return (
          <>
            <textarea
              as="textarea"
              rows={3}
              className="form-control"
              value={value}
              onChange={(e) => handleLocalChange(item.id, e.target.value)}
              onBlur={(e) => saveResult(item.id, e.target.value)}
              disabled={isReadOnly}
            />
          </>
        );

      case "dropdown":
        return (
          <Select
            classNamePrefix="react-select"
            options={
              Array.isArray(item.data)
                ? item.data.map((v) => ({ value: v, label: v }))
                : []
            }
            value={
              Array.isArray(item.data)
                ? item.data
                    .map((v) => ({ value: v, label: v }))
                    .find((opt) => opt.value === value) || null
                : null
            }
            onChange={(opt) => {
              const val = opt ? opt.value : "";
              handleLocalChange(item.id, val);
              saveResult(item.id, val);
            }}
            isDisabled={isReadOnly}
          />
        );
      case "dropdownmulti": {
        const opts = toStringArray(item.data);
        const configOptions = toStringArray(item.configData?.options);
        const options = opts.length ? opts : configOptions;
        const multiselectRaw = item?.configData?.multiselect;
        const multiselect = Number.isFinite(Number(multiselectRaw))
          ? Number(multiselectRaw)
          : 1;
        const selectedValues = toStringArray(value, {
          splitCsv: multiselect === 0,
        });
        const displayValues =
          multiselect === 1
            ? selectedValues
            : selectedValues[0]
              ? [selectedValues[0]]
              : [];
        const selectOptions = options.map((option) => ({
          value: option,
          label: option,
        }));
        const selectedOption =
          multiselect === 1
            ? selectOptions.filter((opt) => selectedValues.includes(opt.value))
            : selectOptions.find(
                (opt) => opt.value === (displayValues[0] || ""),
              ) || null;

        return (
          <Select
            classNamePrefix="react-select"
            options={selectOptions}
            value={selectedOption}
            onChange={(opt) => {
              const nextSelected = toStringArray(
                Array.isArray(opt)
                  ? opt.map((entry) => entry.value)
                  : opt
                    ? [opt.value]
                    : [],
                { splitCsv: multiselect === 0 },
              );
              const nextValue =
                multiselect === 1 ? nextSelected : nextSelected[0] || "";
              handleLocalChange(item.id, nextValue);
              saveResult(item.id, JSON.stringify(nextValue));
            }}
            isMulti={multiselect === 1}
            isDisabled={isReadOnly}
            closeMenuOnSelect={multiselect !== 1}
            placeholder={displayValues.length > 0 ? undefined : "Select..."}
          />
        );
      }
      case "customerdetails":
        return (
          <Select
            classNamePrefix="react-select"
            options={customers.map((c) => ({ value: c.id, label: c.name }))}
            value={
              customers
                .map((c) => ({ value: c.id, label: c.name }))
                .find((opt) => opt.label === value) || null
            }
            onChange={async (opt) => {
              const val = opt ? opt.label : "";
              handleLocalChange(item.id, val);
              await saveResult(item.id, val);
              if (opt) {
                try {
                  await updateWorkOrderForm(workOrderFormId, {
                    customerId: opt.value,
                    updatedAt: new Date().toISOString(),
                    updatedBy: userProfile?.id,
                  });
                } catch (err) {
                  console.error(
                    "Error saving customer to work order form",
                    err,
                  );
                }
              }
            }}
            isDisabled={isReadOnly}
          />
        );
      case "projectdetails":
        return (
          <Select
            classNamePrefix="react-select"
            options={projects.map((p) => ({
              value: p.id,
              label: `${p.customer?.name ?? ""} - ${p.name}`,
              customerId: p.customerId,
            }))}
            value={
              projects
                .map((p) => ({
                  value: p.id,
                  label: `${p.customer?.name ?? ""} - ${p.name}`,
                  customerId: p.customerId,
                }))
                .find(
                  (opt) => opt.value === item.projectId || opt.label === value,
                ) || null
            }
            onChange={async (opt) => {
              const val = opt ? opt.label : "";
              handleLocalChange(item.id, val, {
                projectId: opt ? opt.value : null,
                customerId: opt ? opt.customerId : null,
              });
              await saveResult(item.id, val, {
                projectId: opt ? opt.value : null,
                customerId: opt ? opt.customerId : null,
              });
              await updateWorkOrderForm(workOrderFormId, {
                projectId: opt ? opt.value : null,
                customerId: opt ? opt.customerId : null,
                updatedAt: new Date().toISOString(),
                updatedBy: userProfile?.id,
              });
            }}
            isDisabled={isReadOnly}
          />
        );
      case "areadetails":
        return (
          <Select
            classNamePrefix="react-select"
            options={areas.map((a) => ({ value: a.name, label: a.name }))}
            value={
              areas
                .map((a) => ({ value: a.name, label: a.name }))
                .find((opt) => opt.value === value) || null
            }
            onChange={(opt) => {
              const val = opt ? opt.value : "";
              handleLocalChange(item.id, val);
              saveResult(item.id, val);
            }}
            isDisabled={isReadOnly}
          />
        );
      case "assetdetails":
        return (
          <AsyncSelect
            classNamePrefix="react-select"
            cacheOptions
            defaultOptions={assetOptions.length ? assetOptions : true}
            loadOptions={handleAssetSearch}
            value={item.selectedAsset || null}
            onChange={(opt) => handleAssetSelect(item.id, opt)}
            onMenuScrollToBottom={handleAssetMenuScrollToBottom}
            placeholder="Start typing to search ..."
            isClearable
            isDisabled={isReadOnly}
            isLoading={assetLoading}
          />
        );
      case "meterreading": {
        const assetId = workOrderForm?.assetId || selectedAssetId || null;
        return (
          <MeterReadingField
            item={item}
            assetId={assetId}
            resolveMeterForAsset={resolveMeterForAsset}
            isReadOnly={isReadOnly}
            onLocalChange={handleLocalChange}
            onSave={saveResult}
          />
        );
      }
      case "note":
        return (
          <textarea
            className="form-control"
            rows={3}
            value={value}
            onChange={(e) => handleLocalChange(item.id, e.target.value)}
            onBlur={(e) => saveResult(item.id, e.target.value)}
            disabled={isReadOnly}
          />
        );
      case "date":
        return (
          <input
            type="date"
            className="form-control"
            value={value}
            onChange={(e) => {
              handleLocalChange(item.id, e.target.value);
              saveResult(item.id, e.target.value);
            }}
            disabled={isReadOnly}
          />
        );
      case "datetime":
      case "datetime-local":
        return (
          <input
            type="datetime-local"
            className="form-control"
            value={value}
            onChange={(e) => handleLocalChange(item.id, e.target.value)}
            onBlur={(e) => saveResult(item.id, e.target.value)}
            disabled={isReadOnly}
          />
        );
      case "checkbox":
      case "switch":
        return (
          <>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={value === "true" || value === "1"}
                onChange={(e) => {
                  const val = e.target.checked ? "true" : "false";
                  handleLocalChange(item.id, val);
                  saveResult(item.id, val);
                }}
                disabled={isReadOnly}
              />
            </div>
          </>
        );
      case "photo":
        return (
          <div>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.heic"
              multiple
              style={{ display: "none" }}
              id={`photo-input-${item.id}`}
              onChange={(e) => {
                handlePhotoUpload(item.id, e.target.files);
                e.target.value = "";
              }}
              disabled={isReadOnly}
            />
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 mt-12"
              style={{ borderStyle: "dashed" }}
              onClick={() =>
                document.getElementById(`photo-input-${item.id}`)?.click()
              }
              disabled={isReadOnly}
            >
              <Icon icon="mage:image" className="icon fs-6 text-black" />
              <span className="text-black">+ Add Photo</span>
            </button>
            {Array.isArray(item.resultImages) &&
              item.resultImages.length > 0 && (
                <div className="mt-12 d-flex gap-2 flex-wrap">
                  {item.resultImages.map((img, idx) => (
                    <div
                      key={idx}
                      style={{
                        position: "relative",
                        width: "100%",
                        maxHeight: "400px",
                      }}
                    >
                      <img
                        src={`${process.env.REACT_APP_SPACES_BASE}/${img}`}
                        alt="uploaded"
                        style={{
                          width: "100%",
                          maxHeight: "400px",
                          objectFit: "cover",
                          borderRadius: 6,
                          marginTop: 8,
                        }}
                        onClick={() => openResultImageModal(item.id, idx)}
                        onError={(e) => {
                          const imgEl = e.currentTarget;
                          if (!imgEl) return;

                          // Stop immediately if we've already handled this error
                          if (imgEl.dataset.failed === "1") {
                            return;
                          }

                          imgEl.dataset.failed = "1";

                          // Optional: swap to a placeholder or just remove the src
                          imgEl.src = "/img/image-missing.png";
                        }}
                      />
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => handleRemovePhoto(item.id, idx)}
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: "none",
                            background: "red",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          <Icon icon="mdi:close" width={12} height={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </div>
        );

      case "signature":
        return <SignatureField item={item} value={value} />;
      case "yesno":
        return (
          <>
            <div className="d-flex gap-1">
              <button
                className={`btn btn-sm ${value === 1 || value === "1" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => handleResultOptionSelect(item, 1, "Yes")}
                disabled={isReadOnly}
              >
                Yes
              </button>
              <button
                className={`btn btn-sm ${value === 0 || value === "0" ? "btn-danger" : "btn-outline-danger"}`}
                onClick={() => handleResultOptionSelect(item, 0, "No")}
                disabled={isReadOnly}
              >
                No
              </button>
            </div>
            {renderDefectIndicator(item)}
          </>
        );
      case "workrequest": {
        const workRequestEntries = renderWorkRequestList(item);
        return (
          <div className="d-flex flex-column gap-2">
            {!isReadOnly && (
              <button
                type="button"
                className="btn btn-primary btn-sm align-self-start"
                onClick={() => openNotificationModalForItem(item)}
              >
                Add Work Request
              </button>
            )}
            {workRequestEntries && workRequestEntries.length > 0 && (
              <div className="d-flex flex-column">{workRequestEntries}</div>
            )}
          </div>
        );
      }
      case "okdefect":
        return (
          <>
            <div className="d-flex gap-1">
              <button
                className={`btn btn-sm ${value === 1 || value === "1" || value === "ok" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => handleResultOptionSelect(item, 1, "OK")}
                disabled={isReadOnly}
              >
                OK
              </button>
              <button
                className={`btn btn-sm ${value === 0 || value === "0" || value === "defect" ? "btn-danger" : "btn-outline-danger"}`}
                onClick={() => handleResultOptionSelect(item, 0, "Defect")}
                disabled={isReadOnly}
              >
                Defect
              </button>
            </div>
            {renderDefectIndicator(item)}
          </>
        );
      case "passfail":
        return (
          <>
            <div className="d-flex gap-1">
              <button
                className={`btn btn-sm ${value === "pass" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => handleResultOptionSelect(item, "pass", "Pass")}
                disabled={isReadOnly}
              >
                Pass
              </button>
              <button
                className={`btn btn-sm ${value === "fail" ? "btn-danger" : "btn-outline-danger"}`}
                onClick={() => handleResultOptionSelect(item, "fail", "Fail")}
                disabled={isReadOnly}
              >
                Fail
              </button>
              <button
                className={`btn btn-sm ${value === "review" ? "btn-warning" : "btn-outline-warning"}`}
                onClick={() =>
                  handleResultOptionSelect(item, "review", "Review")
                }
                disabled={isReadOnly}
              >
                Review
              </button>
            </div>
            {renderDefectIndicator(item)}
          </>
        );
      default:
        return null;
    }
  };

  const handleComplete = async () => {
    const incomplete = [];
    items.forEach((it) => {
      const type = getInputType(it.formInputTypeId);
      if (type === "photo") {
        const { minimumFromData, requiredCount } = getPhotoRequirementInfo(it);
        if (requiredCount > 0) {
          const imageCount = Array.isArray(it.resultImages)
            ? it.resultImages.length
            : 0;
          if (imageCount < requiredCount) {
            const message =
              minimumFromData > 0 && imageCount < minimumFromData
                ? getMinimumPhotoValidationMessage(minimumFromData)
                : undefined;
            incomplete.push({ id: it.id, message });
          }
        }
      } else if (it.required === 1 && it.heading === 0) {
        const value = it.result;
        if (!value || String(value).trim() === "") {
          incomplete.push({ id: it.id, name: it, message: undefined });
        }
      }
    });
    if (incomplete.length > 0) {
      const ids = incomplete.map((item) => item.id);
      setIncompleteItemIds(ids);
      setIncompleteItemMessages(() => {
        const next = {};
        incomplete.forEach(({ id, message }) => {
          if (message) {
            next[id] = message;
          }
        });
        return next;
      });
      document
        .getElementById(`form-item-${ids[0]}`)
        ?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    setIncompleteItemIds([]);
    setIncompleteItemMessages({});
    setIsCompleting(true);
    try {
      const meterItems = items.filter(
        (it) => getInputType(it.formInputTypeId) === "meterreading",
      );
      const meterSavePromises = meterItems
        .map((meterItem) => {
          const raw = meterItem.result;
          if (raw === undefined || raw === null) return null;
          const trimmed = String(raw).trim();
          if (trimmed === "") return null;
          return saveResult(meterItem.id, trimmed);
        })
        .filter(Boolean);
      if (meterSavePromises.length > 0) {
        await Promise.all(meterSavePromises);
      }

      await updateWorkOrderForm(workOrderFormId, {
        completed: true,
        updatedAt: new Date().toISOString(),
        updatedBy: userProfile?.id,
      });
      setFormCompleted(true);
      setIsCompleting(false);
      onClose();
    } catch (err) {
      console.error("Error completing form", err);
      setIsCompleting(false);
    }
  };

  const handleReopen = async () => {
    try {
      await updateWorkOrderForm(workOrderFormId, {
        completed: false,
        updatedAt: new Date().toISOString(),
        updatedBy: userProfile?.id,
      });
      setFormCompleted(false);
    } catch (err) {
      console.error("Error reopening form", err);
    }
  };

  const resultImages = useMemo(
    () =>
      items.flatMap((item) => {
        if (
          !Array.isArray(item.resultImages) ||
          item.resultImages.length === 0
        ) {
          return [];
        }

        return item.resultImages
          .filter(Boolean)
          .map((imageKey, imageIndex) => ({
            itemId: item.id,
            itemDescription: item.description,
            imageIndex,
            imageKey,
          }));
      }),
    [items],
  );

  const groupedResultImages = useMemo(() => {
    const groups = [];

    resultImages.forEach((entry, index) => {
      const existingGroup = groups.find(
        (group) => group.itemId === entry.itemId,
      );
      const image = { ...entry, resultIndex: index };

      if (existingGroup) {
        existingGroup.images.push(image);
        return;
      }

      groups.push({
        itemId: entry.itemId,
        itemDescription: entry.itemDescription,
        images: [image],
      });
    });

    return groups;
  }, [resultImages]);

  const resultImageIndexLookup = useMemo(() => {
    const lookup = new Map();
    resultImages.forEach((entry, index) => {
      lookup.set(`${entry.itemId}:${entry.imageIndex}`, index);
    });
    return lookup;
  }, [resultImages]);

  const selectedResultImage = resultImages[selectedResultImageIndex] || null;

  const openResultImageModal = useCallback(
    (itemId, imageIndex) => {
      const entryIndex = resultImageIndexLookup.get(`${itemId}:${imageIndex}`);
      if (entryIndex === undefined) {
        return;
      }
      setSelectedResultImageIndex(entryIndex);
      setResultImageZoom(1);
      setResultImagePan({ x: 0, y: 0 });
      setShowResultImageModal(true);
    },
    [resultImageIndexLookup],
  );

  const closeResultImageModal = useCallback(() => {
    setShowResultImageModal(false);
    setResultImageZoom(1);
    setResultImagePan({ x: 0, y: 0 });
    setIsDraggingResultImage(false);
  }, []);

  const zoomResultImageIn = useCallback(() => {
    setResultImageZoom((prev) => Math.min(4, Number((prev + 0.25).toFixed(2))));
  }, []);

  const zoomResultImageOut = useCallback(() => {
    setResultImageZoom((prev) =>
      Math.max(0.5, Number((prev - 0.25).toFixed(2))),
    );
  }, []);

  const handleMainResultImageWheel = useCallback((event) => {
    event.preventDefault();
    if (event.deltaY < 0) {
      setResultImageZoom((prev) =>
        Math.min(4, Number((prev + 0.1).toFixed(2))),
      );
      return;
    }

    setResultImageZoom((prev) =>
      Math.max(0.5, Number((prev - 0.1).toFixed(2))),
    );
  }, []);

  const showPreviousResultImage = useCallback(() => {
    setSelectedResultImageIndex((prev) => Math.max(0, prev - 1));
    setResultImageZoom(1);
    setResultImagePan({ x: 0, y: 0 });
  }, []);

  const showNextResultImage = useCallback(() => {
    setSelectedResultImageIndex((prev) =>
      Math.min(resultImages.length - 1, prev + 1),
    );
    setResultImageZoom(1);
    setResultImagePan({ x: 0, y: 0 });
  }, [resultImages.length]);

  const handleResultImageDragStart = useCallback(
    (event) => {
      if (!canPanResultImage || event.button !== 0) {
        return;
      }
      event.preventDefault();
      resultImageDragOffsetRef.current = {
        x: event.clientX - resultImagePan.x,
        y: event.clientY - resultImagePan.y,
      };
      setIsDraggingResultImage(true);
    },
    [canPanResultImage, resultImagePan.x, resultImagePan.y],
  );

  useEffect(() => {
    if (!isDraggingResultImage) {
      return undefined;
    }

    const handlePointerMove = (event) => {
      const desiredX = event.clientX - resultImageDragOffsetRef.current.x;
      const desiredY = event.clientY - resultImageDragOffsetRef.current.y;
      const clamped = clampResultImagePan(desiredX, desiredY);
      setResultImagePan({ x: clamped.x, y: clamped.y });
      setCanPanResultImage(clamped.canPan);
    };

    const handlePointerUp = () => {
      setIsDraggingResultImage(false);
    };

    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", handlePointerUp);

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
    };
  }, [clampResultImagePan, isDraggingResultImage]);

  const syncResultImagePanBounds = useCallback(() => {
    const clamped = clampResultImagePan(resultImagePan.x, resultImagePan.y);
    setResultImagePan((prev) => {
      if (prev.x === clamped.x && prev.y === clamped.y) {
        return prev;
      }
      return { x: clamped.x, y: clamped.y };
    });
    setCanPanResultImage(clamped.canPan);
  }, [clampResultImagePan, resultImagePan.x, resultImagePan.y]);

  useEffect(() => {
    if (!showResultImageModal) {
      return undefined;
    }

    syncResultImagePanBounds();
    window.addEventListener("resize", syncResultImagePanBounds);
    return () => window.removeEventListener("resize", syncResultImagePanBounds);
  }, [
    resultImageZoom,
    selectedResultImageIndex,
    showResultImageModal,
    syncResultImagePanBounds,
  ]);

  useEffect(() => {
    if (resultImages.length === 0) {
      setShowResultImageModal(false);
      setSelectedResultImageIndex(0);
      return;
    }

    if (selectedResultImageIndex > resultImages.length - 1) {
      setSelectedResultImageIndex(resultImages.length - 1);
    }
  }, [resultImages, selectedResultImageIndex]);

  useEffect(() => {
    if (!showResultImageModal) {
      return;
    }

    const listContainer = resultImageListRef.current;
    const selectedGroupHeader =
      resultImageGroupHeaderRefs.current[selectedResultImage?.itemId];
    const selectedThumbnail =
      resultImageThumbnailRefs.current[selectedResultImageIndex];

    if (!listContainer || (!selectedGroupHeader && !selectedThumbnail)) {
      return;
    }

    listContainer.scrollTo({
      top: (selectedGroupHeader || selectedThumbnail).offsetTop,
      behavior: "smooth",
    });
  }, [
    selectedResultImage?.itemId,
    selectedResultImageIndex,
    showResultImageModal,
  ]);

  if (!form) return null;

  const notificationModalContent = notificationLoading
    ? [
        <div className="col-12" key="loading">
          Loading notification…
        </div>,
      ]
    : notificationFields.map(renderNotificationField);

  const notificationModalTitle = notificationEditingId
    ? notificationForm.notificationTitle || "Edit Work Request"
    : "Add Work Request";

  return (
    <div className="modal modal-backdrop show d-block" tabIndex="-1">
      <div className="modal-dialog modal-lg">
        <div className="modal-content border-0 bg-transparent">
          <div
            className="card"
            style={{
              margin: "auto",
              boxShadow: "0 0.25rem 1rem rgba(46,46,46,0.25)",
            }}
          >
            <div className="card-header bg-primary text-white">
              <div className="row">
                <div className="col-md-6 mb-12">
                  <div className="fs-5 text-white">{form.name}</div>
                </div>
                <div className="col-md-6 text-end">
                  <button
                    type="button"
                    className="btn btn-warning btn-sm"
                    onClick={handleExportPdf}
                    disabled={isExportingPdf}
                  >
                    {isExportingPdf ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-8"
                          role="status"
                          aria-hidden="true"
                        />
                        Exporting PDF
                      </>
                    ) : (
                      "Export to PDF"
                    )}
                  </button>
                  {/*<button type="button" className="btn-close btn-close-white" onClick={onClose} />*/}
                </div>
              </div>
              <div className="col-md-12">
                <span className="text-sm">{form.description}</span>
              </div>
            </div>
            <div
              className="card-body"
              style={{ maxHeight: "78vh", overflowY: "auto" }}
            >
              <div className="row g-3">
                <div className="col-12 mb-16">
                  <label className="form-label">Form Name</label>
                  <input
                    type="text"
                    className="form-control"
                    maxLength={100}
                    value={workOrderForm?.formName || form?.name || ""}
                    onChange={(e) =>
                      setWorkOrderForm((prev) => ({
                        ...(prev || {}),
                        formName: e.target.value,
                      }))
                    }
                    onBlur={async (e) => {
                      try {
                        await updateWorkOrderForm(workOrderFormId, {
                          formName: e.target.value || null,
                          updatedAt: new Date().toISOString(),
                          updatedBy: userProfile?.id,
                        });
                      } catch (err) {
                        console.error("Error saving form name", err);
                      }
                    }}
                    disabled={isReadOnly}
                  />
                </div>
                {items.map((it) =>
                  it.heading === 1 ? (
                    <div key={it.id} className="col-12 py-8">
                      <div className="fw-bold fs-6">{it.description}</div>
                    </div>
                  ) : (
                    <div
                      key={it.id}
                      className={`col-md-${it.width} mb-16`}
                      id={`form-item-${it.id}`}
                    >
                      <div
                        className={`p-12 border radius-6 ${incompleteItemIds.includes(it.id) ? "bg-danger-100" : ""}`}
                      >
                        <div>
                          {it.description}
                          {Boolean(it.required) && (
                            <span className="text-danger ms-4">*</span>
                          )}
                        </div>
                        <div className="text-sm text-muted">
                          {it.instructions}
                        </div>
                        <div className="mt-8">{renderInput(it)}</div>
                        {incompleteItemMessages[it.id] && (
                          <div className="mt-8 text-sm text-danger">
                            {incompleteItemMessages[it.id]}
                          </div>
                        )}
                        {it.includePhoto === 1 && (
                          <div className="mt-8">
                            <input
                              type="file"
                              accept=".png,.jpg,.jpeg,.heic"
                              multiple
                              id={`photo-input-${it.id}`}
                              style={{ display: "none" }}
                              onChange={(e) => {
                                handlePhotoUpload(it.id, e.target.files);
                                e.target.value = "";
                              }}
                              disabled={isReadOnly}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 mt-12"
                              style={{ borderStyle: "dashed" }}
                              onClick={() =>
                                document
                                  .getElementById(`photo-input-${it.id}`)
                                  ?.click()
                              }
                              disabled={isReadOnly}
                            >
                              <Icon
                                icon="mage:image"
                                className="icon fs-6 text-black"
                              />
                              <span className="text-black">+ Add Photo</span>
                            </button>
                            {Array.isArray(it.resultImages) &&
                              it.resultImages.length > 0 && (
                                <div className="mt-12 d-flex gap-2 flex-wrap">
                                  {it.resultImages.map((img, idx) => (
                                    <div
                                      key={idx}
                                      style={{
                                        position: "relative",
                                        width: "100%",
                                        maxHeight: "800px",
                                      }}
                                    >
                                      <img
                                        src={`${process.env.REACT_APP_SPACES_BASE}/${img}`}
                                        alt="uploaded"
                                        style={{
                                          width: "100%",
                                          maxHeight: "800px",
                                          objectFit: "cover",
                                          borderRadius: 6,
                                          marginTop: 8,
                                        }}
                                        onClick={() =>
                                          openResultImageModal(it.id, idx)
                                        }
                                      />
                                      {!isReadOnly && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleRemovePhoto(it.id, idx)
                                          }
                                          style={{
                                            position: "absolute",
                                            top: -6,
                                            right: -6,
                                            width: 20,
                                            height: 20,
                                            borderRadius: "50%",
                                            border: "none",
                                            background: "red",
                                            color: "#fff",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                          }}
                                        >
                                          <Icon
                                            icon="mdi:close"
                                            width={12}
                                            height={12}
                                          />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                          </div>
                        )}
                        {!it.disableComments && (
                          <div className="mt-8">
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              onClick={() => handleCommentToggle(it.id)}
                              disabled={isReadOnly}
                            >
                              <Icon
                                icon="mage:plus"
                                className="text-lg me-4"
                                style={{ position: "relative", top: "-2px" }}
                              />
                              <span className="text-sm">Add Comment</span>
                            </button>
                            {it.showComment && (
                              <textarea
                                className="form-control mt-2"
                                rows={3}
                                value={it.comments || ""}
                                onChange={(e) =>
                                  handleCommentChange(it.id, e.target.value)
                                }
                                onBlur={(e) =>
                                  saveComment(it.id, e.target.value)
                                }
                                disabled={isReadOnly}
                              />
                            )}
                          </div>
                        )}
                        {Array.isArray(it.images) && it.images.length > 0 && (
                          <div className="mt-16 d-flex">
                            <div style={{ width: "100%", paddingRight: "6px" }}>
                              <img
                                src={
                                  it.images?.[0]
                                    ? `${process.env.REACT_APP_SPACES_BASE}/${it.images[0]}`
                                    : ""
                                }
                                alt="form item"
                                style={{
                                  width: "100%",
                                  maxHeight: "400px",
                                  objectFit: "cover",
                                  borderRadius: "6px",
                                  display: "block",
                                }}
                              />
                            </div>
                            {it.images?.[1] && (
                              <div style={{ width: "50%", paddingLeft: "6px" }}>
                                <img
                                  src={
                                    it.images?.[1]
                                      ? `${process.env.REACT_APP_SPACES_BASE}/${it.images[1]}`
                                      : ""
                                  }
                                  alt="form item"
                                  style={{
                                    width: "100%",
                                    maxHeight: "200px",
                                    objectFit: "cover",
                                    borderRadius: "6px",
                                    display: "block",
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
              <div className="border-top pt-3 mt-3">
                {workOrderForm?.createdAt && createdByUser && (
                  <div className="d-flex align-items-center justify-content-start mt-2">
                    <img
                      src={
                        createdByUser.userImage
                          ? `${process.env.REACT_APP_SPACES_BASE}/${createdByUser.userImage}`
                          : `${process.env.REACT_APP_SPACES_BASE}/user.png`
                      }
                      alt="Created By"
                      style={{
                        width: "45px",
                        height: "45px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "1px solid #ddd",
                      }}
                    />
                    <span className="ms-12">
                      Created By&nbsp;
                      <strong>{createdByUser.name}</strong>,
                      <span className="text-muted text-sm ps-4">
                        {new Date(workOrderForm.createdAt).toLocaleString(
                          "en-GB",
                          {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </span>
                  </div>
                )}
                {workOrderForm?.updatedAt && updatedByUser && (
                  <div className="d-flex align-items-center justify-content-start mt-12">
                    <img
                      src={
                        updatedByUser.userImage
                          ? `${process.env.REACT_APP_SPACES_BASE}/${updatedByUser.userImage}`
                          : `${process.env.REACT_APP_SPACES_BASE}/user.png`
                      }
                      alt="Updated By"
                      style={{
                        width: "45px",
                        height: "45px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "1px solid #ddd",
                      }}
                    />
                    <span className="ms-12">
                      Completed By&nbsp;
                      <strong>{updatedByUser.name}</strong>,
                      <span className="text-muted text-sm ps-4">
                        {new Date(workOrderForm.updatedAt).toLocaleString(
                          "en-GB",
                          {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="card-footer bg-white text-end">
              <button className="btn btn-secondary me-8" onClick={onClose}>
                Close
              </button>
              {!readOnly &&
                (formCompleted ? (
                  <button className="btn btn-warning" onClick={handleReopen}>
                    Reopen Form
                  </button>
                ) : (
                  <button
                    className="btn btn-success my-12"
                    onClick={handleComplete}
                    disabled={isCompleting}
                  >
                    {isCompleting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-8"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving Form
                      </>
                    ) : (
                      "Complete Form"
                    )}
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>
      {showNotificationModal && (
        <NotificationFormModal
          show={showNotificationModal}
          title={notificationModalTitle}
          onClose={closeNotificationModal}
          onSave={handleNotificationSave}
          saveDisabled={notificationSaving || notificationLoading}
        >
          {notificationModalContent}
        </NotificationFormModal>
      )}
      {notificationImageModalSrc && (
        <div
          className="modal modal-black show d-block"
          onClick={() => setNotificationImageModalSrc(null)}
          tabIndex="-1"
        >
          <div className="modal-dialog" style={{ maxWidth: "75%" }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <img
                src={notificationImageModalSrc}
                alt="view"
                style={{ width: "100%" }}
              />
            </div>
          </div>
        </div>
      )}
      {showResultImageModal && selectedResultImage && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          onClick={closeResultImageModal}
          style={{
            backgroundColor: "rgba(0, 0, 0, 1)",
            zIndex: 1080,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              display: "flex",
              height: "100vh",
              width: "100vw",
            }}
          >
            <div
              ref={resultImageListRef}
              style={{
                width: "20vw",
                minWidth: 220,
                maxWidth: 360,
                overflowY: "auto",
                padding: 12,
                borderRight: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              {groupedResultImages.map((group) => (
                <div key={`result-image-group-${group.itemId}`}>
                  <div
                    ref={(element) => {
                      if (!element) {
                        delete resultImageGroupHeaderRefs.current[group.itemId];
                        return;
                      }
                      resultImageGroupHeaderRefs.current[group.itemId] =
                        element;
                    }}
                    className="text-md fw-semibold"
                    style={{
                      color: "#111",
                      backgroundColor: "#fff",
                      lineHeight: 1.2,
                      padding: "10px 12px",
                      borderRadius: 4,
                      marginBottom: 10,
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    {group.itemDescription}
                  </div>
                  {group.images.map((entry) => {
                    const imageUrl = `${process.env.REACT_APP_SPACES_BASE}/${entry.imageKey}`;
                    const isSelected =
                      entry.resultIndex === selectedResultImageIndex;

                    return (
                      <button
                        key={`${entry.itemId}-${entry.imageIndex}-${entry.imageKey}`}
                        type="button"
                        ref={(element) => {
                          if (!element) {
                            delete resultImageThumbnailRefs.current[
                              entry.resultIndex
                            ];
                            return;
                          }
                          resultImageThumbnailRefs.current[entry.resultIndex] =
                            element;
                        }}
                        onClick={() => {
                          setSelectedResultImageIndex(entry.resultIndex);
                          setResultImageZoom(1);
                          setResultImagePan({ x: 0, y: 0 });
                        }}
                        style={{
                          width: "100%",
                          border: isSelected
                            ? "2px solid #4da3ff"
                            : "1px solid rgba(255,255,255,0.25)",
                          borderRadius: 6,
                          background: "transparent",
                          padding: 0,
                          marginBottom: 10,
                          cursor: "pointer",
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={`result thumbnail ${entry.resultIndex + 1}`}
                          style={{
                            width: "100%",
                            display: "block",
                            maxHeight: 180,
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                        />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            <div
              ref={resultImageViewportRef}
              style={{
                width: "80vw",
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
              }}
              onWheel={handleMainResultImageWheel}
            >
              <button
                type="button"
                className="btn btn-light"
                onClick={closeResultImageModal}
                style={{
                  position: "absolute",
                  backgroundColor: "rgba(255, 255, 255, 0.6)",
                  border: "none",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                }}
              >
                Close
              </button>
              <div
                style={{
                  position: "absolute",
                  top: 16,
                  right: 100,
                  display: "flex",
                  gap: 8,
                  zIndex: 2,
                }}
              >
                <button
                  type="button"
                  className="btn btn-light radius-20"
                  onClick={zoomResultImageOut}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    border: "none",
                  }}
                >
                  <Icon
                    icon="mdi:minus"
                    style={{ position: "relative", top: "-3px" }}
                  />
                </button>
                <button
                  type="button"
                  className="btn btn-light radius-20"
                  onClick={zoomResultImageIn}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    border: "none",
                  }}
                >
                  <Icon
                    icon="mdi:plus"
                    style={{ position: "relative", top: "-3px" }}
                  />
                </button>
              </div>
              <button
                type="button"
                className="btn btn-light"
                disabled={selectedResultImageIndex === 0}
                onClick={showPreviousResultImage}
                style={{
                  position: "absolute",
                  left: 16,
                  zIndex: 2,
                  backgroundColor: "rgba(255, 255, 255, 0.6)",
                  border: "none",
                }}
              >
                <Icon icon="mdi:chevron-left" width={28} />
              </button>
              <img
                ref={resultImageRef}
                src={`${process.env.REACT_APP_SPACES_BASE}/${selectedResultImage.imageKey}`}
                alt="Selected result"
                onMouseDown={handleResultImageDragStart}
                onDragStart={(event) => event.preventDefault()}
                onLoad={syncResultImagePanBounds}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  transform: `translate(${resultImagePan.x}px, ${resultImagePan.y}px) scale(${resultImageZoom})`,
                  transformOrigin: "center center",
                  transition: isDraggingResultImage
                    ? "none"
                    : "transform 0.1s ease-out",
                  cursor: canPanResultImage
                    ? isDraggingResultImage
                      ? "grabbing"
                      : "grab"
                    : "default",
                  userSelect: "none",
                }}
              />
              <button
                type="button"
                className="btn btn-light"
                disabled={selectedResultImageIndex === resultImages.length - 1}
                onClick={showNextResultImage}
                style={{
                  position: "absolute",
                  right: 16,
                  zIndex: 2,
                  backgroundColor: "rgba(255, 255, 255, 0.6)",
                  border: "none",
                }}
              >
                <Icon icon="mdi:chevron-right" width={28} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormViewModal;
